import Chart from 'react-google-charts';
import * as Styled from './styles';

type ChartVariant = 'line' | 'pie' | 'bar' | 'column';

type MetricChartProps = {
  title: string;
  subtitle?: string;
  variant: ChartVariant;
  data: (string | number | { v: number; f: string } | { type: string; label?: string; role?: string })[][];
  height?: number;
  options?: { hAxis?: Record<string, unknown>; chartArea?: { height?: number } };
};

const colors = ['#017ac6', '#f39c35', '#22a06b', '#6f5bd3', '#e25555', '#009596', '#8a5a44', '#607d8b'];

function buildOptions(variant: ChartVariant, options: MetricChartProps['options'] = {}) {
  const commonOptions = {
    backgroundColor: 'transparent',
    colors,
    fontName: 'Inter',
    fontSize: 12,
    tooltip: { textStyle: { fontName: 'Inter', fontSize: 12 } },
  };

  if (variant === 'pie') {
    return {
      ...commonOptions,
      chartArea: {
        left: '10%',
        right: '10%',
        top: 16,
        height: '76%',
      },
      legend: {
        position: 'bottom',
        alignment: 'center',
        textStyle: { color: '#455a64', fontSize: 12 },
      },
      pieHole: 0.48,
      pieSliceText: 'value',
      sliceVisibilityThreshold: 0,
    };
  }

  const axisOptions = {
    gridlines: { color: '#e8ebf2' },
    baselineColor: '#cdd3df',
    textStyle: { color: '#607d8b', fontSize: 11 },
    titleTextStyle: { color: '#455a64', italic: false, fontSize: 12 },
  };

  if (variant === 'bar') {
    return {
      ...commonOptions,
      chartArea: { left: 150, right: 100, top: 12, height: '82%', ...options.chartArea },
      legend: { position: 'none' },
      hAxis: { ...axisOptions, minValue: 0, format: '#', title: 'Quantidade' },
      vAxis: { ...axisOptions, gridlines: { color: 'transparent' } },
    };
  }

  if (variant === 'column') {
    return {
      ...commonOptions,
      chartArea: { left: 54, top: 16, width: '84%', height: '70%' },
      legend: { position: 'none' },
      hAxis: { ...axisOptions, gridlines: { color: 'transparent' }, slantedText: true, slantedTextAngle: 28 },
      vAxis: { ...axisOptions, minValue: 0, format: '#', title: 'Sessões' },
    };
  }

  return {
    ...commonOptions,
    chartArea: { left: 54, right: 25, top: 16, height: '72%' },
    curveType: 'none',
    legend: { position: 'none' },
    lineWidth: 3,
    pointSize: 5,
    hAxis: { ...axisOptions, gridlines: { color: 'transparent' }, slantedText: true, slantedTextAngle: 28, ...options.hAxis },
    vAxis: { ...axisOptions, minValue: 0, format: '#', title: 'Acessos' },
  };
}

export function MetricChart({ title, subtitle, variant, data, height = 360, options }: MetricChartProps) {
  const hasData = data.slice(1).some(row => typeof row[1] === 'number' && Number.isFinite(row[1]) && (variant === 'line' || row[1] > 0));
  const chartType = variant === 'line' ? 'LineChart' : variant === 'pie' ? 'PieChart' : variant === 'bar' ? 'BarChart' : 'ColumnChart';

  return (
    <Styled.ChartCard>
      <Styled.ChartHeader>
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </Styled.ChartHeader>
      {hasData ? (
        <Styled.ChartArea role="img" aria-label={title} $height={height}>
          <Chart
            chartType={chartType}
            width="100%"
            height={`${height}px`}
            data={data}
            options={buildOptions(variant, options)}
            chartLanguage="pt-BR"
            loader={<Styled.ChartLoading>Carregando gráfico...</Styled.ChartLoading>}
          />
        </Styled.ChartArea>
      ) : (
        <Styled.EmptyChart $height={height}>
          <strong>Sem dados no período</strong>
          <span>Altere os filtros para consultar outro intervalo.</span>
        </Styled.EmptyChart>
      )}
    </Styled.ChartCard>
  );
}
